import React, { useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { WorkspaceNameCard } from '@/components/settings/workspace/WorkspaceNameCard';
import { TipPolicyCard } from '@/components/settings/workspace/TipPolicyCard';
import { OpenLinkSettingsCard } from '@/components/settings/workspace/OpenLinkSettingsCard';
import { BrandIdentityCard } from '@/components/settings/workspace/BrandIdentityCard';
import { WebhookSettingsCard } from '@/components/settings/workspace/WebhookSettingsCard';
import { MarketingToolsCard } from '@/components/settings/workspace/MarketingToolsCard';
import { AutomationHealthCard } from '@/components/settings/workspace/AutomationHealthCard';
import { MembersList } from '@/components/settings/workspace/MembersList';
import { DeleteWorkspaceCard } from '@/components/settings/workspace/DeleteWorkspaceCard';
import { ReferralCard } from '@/components/settings/workspace/ReferralCard';
import { Skeleton } from '@/components/ui/skeleton';

const WorkspaceSettings: React.FC = () => {
  const { userSettings, fetchUserSettings } = useAuth();
  
  const handleUpdate = useCallback(async () => {
    await fetchUserSettings();
  }, [fetchUserSettings]);

  if (!userSettings?.activeWorkspace) {
    return (
        <div className="space-y-6">
            <Skeleton className="h-40 w-full rounded-[2rem]" />
            <Skeleton className="h-60 w-full rounded-[2rem]" />
        </div>
    );
  }

  const { activeWorkspace, activeWorkspaceId, userId } = userSettings;
  
  // Determine role from workspaces array
  const currentRole = userSettings.workspaces.find(ws => ws.id === activeWorkspaceId)?.role || 'viewer';
  const isOwner = currentRole === 'owner';

  return (
    <div className="space-y-8 pb-12">
      <div className="space-y-6">
        <div>
          <p className="mb-3 inline-flex rounded-full border border-slate-200 bg-white px-4 py-2 text-[0.72rem] font-semibold uppercase tracking-[0.08em] text-slate-600">
            Studio
          </p>
          <h2 className="text-xl font-semibold tracking-[-0.02em] text-slate-900">Studio Details</h2>
        </div>
        <WorkspaceNameCard 
            workspaceId={activeWorkspaceId} 
            currentName={activeWorkspace.name} 
            onUpdate={handleUpdate} 
        />
        <TipPolicyCard
          workspaceId={activeWorkspaceId}
          activeWorkspace={activeWorkspace}
          onUpdate={handleUpdate}
        />
        <OpenLinkSettingsCard
          workspaceId={activeWorkspaceId}
          activeWorkspace={activeWorkspace}
          onUpdate={handleUpdate}
        />
        <BrandIdentityCard
          workspaceId={activeWorkspaceId}
          activeWorkspace={activeWorkspace}
          onUpdate={handleUpdate}
        />
        <WebhookSettingsCard
          workspaceId={activeWorkspaceId}
          activeWorkspace={activeWorkspace}
          onUpdate={handleUpdate}
        />
        <MarketingToolsCard activeWorkspace={activeWorkspace} />
        <AutomationHealthCard workspaceId={activeWorkspaceId} />
      </div>

      <div className="space-y-6">
        <h2 className="text-xl font-semibold tracking-[-0.02em] text-slate-900">Team Access</h2>
        <MembersList 
            workspaceId={activeWorkspaceId} 
            currentUserRole={currentRole} 
        />
      </div>

      <div className="space-y-6">
        <h2 className="text-xl font-semibold tracking-[-0.02em] text-slate-900">Refer a Friend</h2>
        <ReferralCard
          referralLink={`${window.location.origin}/auth?mode=signup&ref=${activeWorkspace.referralCode || ''}`}
          referralCode={activeWorkspace.referralCode || ''}
        />
      </div>

      {isOwner && (
          <div className="space-y-6 border-t border-slate-200 pt-8">
            <h2 className="text-xl font-semibold tracking-[-0.02em] text-red-700">Danger Zone</h2>
            <DeleteWorkspaceCard 
                workspaceId={activeWorkspaceId} 
                workspaceName={activeWorkspace.name} 
            />
          </div>
      )}
    </div>
  );
};

export default WorkspaceSettings;
